import { createTheme } from '@mui/material/styles';

// Extend MUI's palette with brand colors that aren't expressible as PaletteColor
// entries. Lets `sx` resolve them via theme path strings (e.g.
// `bgcolor: 'colors.successHighlight'`, `borderColor: 'rowDivider'`), symmetric
// with built-ins like `'text.primary'` / `'divider'`.
declare module '@mui/material/styles' {
  interface Palette {
    colors: {
      successHighlight: string;
      // "Mismatch" pill (AI suggestion differs from the entered value). Flat keys so
      // `sx` resolves them via single-level palette paths (e.g. `color: 'colors.mismatchText'`).
      mismatchText: string;
      mismatchBg: string;
      mismatchBorder: string;
      // Dark red for count/alert badges (matches the urgency "most severe" red).
      dangerDark: string;
      // Soft red tint behind escalation/danger pills (prototype .esc-indicator bg).
      dangerBg: string;
      // Claim-type tag pill (blue-grey filled, prototype-derived).
      claimTypeChipBg: string;
      claimTypeChipText: string;
      claimTypeChipBorder: string;
      // "Draft — waiting to send" pill (escalation threads, prototype-derived amber).
      draftText: string;
      draftBg: string;
      // "Response received" escalation signal (prototype-derived teal): a practice reply
      // is awaiting the back office's review.
      receivedText: string;
      receivedBg: string;
      // "Escalation attention" pill (awaiting response state). Amber reads as "attention/awaiting".
      escalationText: string;
      escalationBg: string;
      escalationBorder: string;
      // Escalation section "all responded" state (prototype .escalation-section.all-responded).
      escalationSuccessText: string;
      escalationSuccessBg: string;
      escalationSuccessBorder: string;
      // Escalation section head subtitle text — darker than escalationText/escalationSuccessText
      // (prototype .escalation-section-head .subtitle).
      escalationSubtitleText: string;
      escalationSuccessSubtitleText: string;
      // Escalation "Response sent" status pill (prototype .status-pill.responded).
      escalationRespondedText: string;
      escalationRespondedBg: string;
      // Checklist-derived thread's reason badge (prototype .reason-chip).
      reasonChipBg: string;
      reasonChipText: string;
      // "Your response" message/attachment tint (prototype .thread-msg.mine / .thread-attach).
      replyTintBg: string;
      replyTintBorder: string;
      // Escalation thread's outlined Reply/Upload buttons (prototype .btn-outline).
      escalationButtonText: string;
      escalationButtonBorder: string;
    };
    /** Lighter separator used between table rows (vs the stronger `divider`). */
    rowDivider: string;
  }
  interface PaletteOptions {
    colors?: {
      successHighlight?: string;
      mismatchText?: string;
      mismatchBg?: string;
      mismatchBorder?: string;
      dangerDark?: string;
      dangerBg?: string;
      claimTypeChipBg?: string;
      claimTypeChipText?: string;
      claimTypeChipBorder?: string;
      draftText?: string;
      draftBg?: string;
      receivedText?: string;
      receivedBg?: string;
      escalationText?: string;
      escalationBg?: string;
      escalationBorder?: string;
      escalationSuccessText?: string;
      escalationSuccessBg?: string;
      escalationSuccessBorder?: string;
      escalationSubtitleText?: string;
      escalationSuccessSubtitleText?: string;
      escalationRespondedText?: string;
      escalationRespondedBg?: string;
      reasonChipBg?: string;
      reasonChipText?: string;
      replyTintBg?: string;
      replyTintBorder?: string;
      escalationButtonText?: string;
      escalationButtonBorder?: string;
    };
    rowDivider?: string;
  }
}

/** Alteva black/white brand theme — shared by the portal and backoffice apps. */
export const brandTheme = createTheme({
  palette: {
    primary: {
      main: '#000000', // brand black — CTAs, contained buttons
      light: '#eeeeee', // neutral grey tint (was teal avatar bg)
    },
    action: {
      hover: '#f5f5f5', // grey-100
      selected: '#eeeeee', // grey-200
    },
    secondary: {
      main: '#616161', // grey-700 (was teal)
    },
    info: {
      main: '#757575', // grey-600 (was purple accent)
    },
    colors: {
      successHighlight: '#E9F7ED', // success green tint (was brand teal)
      // "Mismatch" pill — aligned to the urgency palette's red (#D32F2F) with the
      // same soft-tint treatment (text = colour, bg = alpha(colour, .12)).
      mismatchText: '#D32F2F',
      mismatchBg: 'rgba(211, 47, 47, 0.12)',
      mismatchBorder: 'rgba(211, 47, 47, 0.30)',
      // Red for count/alert badges — matches the Incomplete status badge red.
      dangerDark: '#B02A37',
      // Escalation indicator pill background (prototype .esc-indicator).
      dangerBg: '#FBEAEA',
      // Claim-type tag pill — blue-grey filled, from the prototype's encounter list chips.
      claimTypeChipBg: '#eef2f4',
      claimTypeChipText: '#37474f',
      claimTypeChipBorder: '#e3e9ec',
      // Escalation-thread "waiting to send" pill (prototype .et-draft-tag / .ci-status.draft).
      draftText: '#C4841C',
      draftBg: '#FEF8E7',
      // "Response received" escalation signal — prototype teal (.st-received / list signal).
      receivedText: '#128293',
      receivedBg: '#E6F4F6',
      // Escalation attention pill (awaiting response state) — amber accent matching Mismatch pattern.
      escalationText: '#b45309',
      escalationBg: '#fffbeb',
      escalationBorder: '#fcd34d',
      // Escalation section "all responded" state (prototype .escalation-section.all-responded).
      escalationSuccessText: '#158A63',
      escalationSuccessBg: '#E5F4EE',
      escalationSuccessBorder: '#a7d8bf',
      // Escalation section head subtitle text (darker than escalationText/escalationSuccessText).
      escalationSubtitleText: '#7a5b13',
      escalationSuccessSubtitleText: '#1E7E34',
      // Escalation "Response sent" status pill (prototype .status-pill.responded).
      escalationRespondedText: '#128293',
      escalationRespondedBg: '#E6F4F6',
      // Checklist-derived thread's reason badge (prototype .reason-chip).
      reasonChipBg: '#f1f5f9',
      reasonChipText: '#475569',
      // "Your response" message/attachment tint (prototype .thread-msg.mine / .thread-attach).
      replyTintBg: '#f0f7f8',
      replyTintBorder: '#cfe0e3',
      // Escalation thread's outlined Reply/Upload buttons (prototype .btn-outline;
      // prototype's --primary-main/--input-border, distinct from this app's black CTA primary).
      escalationButtonText: '#0a3d47',
      escalationButtonBorder: '#c4c4c4',
    },
    text: {
      primary: '#212121', // grey-900 (near-black)
      secondary: '#757575', // grey-600
    },
    divider: '#e0e0e0', // grey-300
    rowDivider: '#f5f5f5', // grey-100 — table row separators
    background: {
      default: '#fafafa', // grey-50
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", Helvetica, Arial, sans-serif',
  },
  spacing: 5,
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: 13,
          // Default button height across the app. Override per-instance (e.g. the
          // taller sidebar CTA) only where intentionally different.
          minHeight: 36,
          height: 36,
        },
        containedPrimary: {
          backgroundColor: '#000000',
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': { backgroundColor: '#1a1a1a', boxShadow: 'none' },
        },
        outlinedPrimary: {
          backgroundColor: '#ffffff',
          borderColor: '#e0e0e0',
          color: '#212121',
          '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#e0e0e0' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #e0e0e0',
          boxShadow: 'none',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#616161', fontWeight: 500 },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: '#616161', fontWeight: 500 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Default height for single-line small inputs (text fields, selects,
          // autocompletes). Multiline inputs are excluded so they can grow.
          '&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': { height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0', borderWidth: 1 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd', borderWidth: 1 },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000000', borderWidth: 1 },
        },
        input: {
          '&::placeholder': { color: '#9e9e9e', opacity: 1 },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        clearIndicator: { color: '#616161' },
        popupIndicator: { color: '#616161' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '.MuiInputAdornment-root &': { color: '#616161' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: '#616161' },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '.MuiInputAdornment-root &': { color: '#616161' },
        },
      },
    },
  },
});
